import React from 'react';
import { statusTint, useTheme } from '../../lib/theme';

// ============================================================================
// ChoiceKeys — the form-controls standard for a SHORT list (≤ 6 options):
// truck size, crew size, job type, priority, tier, a status inside a
// form. Every option is a visible key, one tap, nothing to open. Replaces
// <select> / <Select> (form-controls.md, "Short list: B").
//
// items: [{ value, label, color?, bg?, icon?, disabled?, title? }]
//   color/bg  — status items: a 3px ribbon on the key's left edge in
//               `color`; when selected the key fills with `bg` and the
//               label takes `color` (Jaylen: "assuming they at least have
//               a ribbon of their correct color").
// value / onChange(value)
// allowEmpty: adds a leading "—" key that emits '' (for optional fields
//   that used to have a blank <option>). Pass emptyLabel to name it.
// size: 'md' (40px) | 'sm' (32px, dense rows)
// wrap: default true. columns: force an even grid (e.g. 3) instead of wrap.
// Past ~6 options this is the wrong control — use Combobox.
// ============================================================================

export default function ChoiceKeys({
    items = [], value, onChange, allowEmpty = false, emptyLabel = '—',
    size = 'md', wrap = true, columns, disabled = false, ariaLabel, className = '', keyClassName = '',
}) {
    const list = allowEmpty ? [{ value: '', label: emptyLabel }, ...items] : items;
    const h = size === 'sm' ? 'h-8 min-w-[36px] px-2.5 text-[12px]' : 'h-10 min-w-[44px] px-3 text-[13px]';
    const layout = columns ? `grid gap-1.5` : `flex ${wrap ? 'flex-wrap' : ''} gap-1.5`;
    const cur = value === null || value === undefined ? '' : String(value);
    const { isDark } = useTheme();

    return (
        <div
            role="radiogroup"
            aria-label={ariaLabel}
            className={`${layout} ${className}`}
            style={columns ? { gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` } : undefined}
        >
            {list.map(it => {
                const v = String(it.value);
                const on = v === cur;
                const isDisabled = disabled || it.disabled;
                const hasColor = !!it.color;
                const style = hasColor
                    ? (on
                        ? { background: statusTint(it.color, it.bg, isDark), color: it.color, borderColor: it.color, boxShadow: `inset 3px 0 0 ${it.color}` }
                        : { boxShadow: `inset 3px 0 0 ${it.color}` })
                    : undefined;
                return (
                    <button
                        key={v}
                        type="button"
                        role="radio"
                        aria-checked={on}
                        title={it.title}
                        disabled={isDisabled}
                        onClick={() => !isDisabled && onChange?.(it.value)}
                        style={style}
                        className={`${h} rounded-lg border font-bold transition-colors select-none inline-flex items-center justify-center gap-1.5 whitespace-nowrap ${
                            on && !hasColor
                                ? 'bg-slate-900 text-white border-slate-900 shadow-[inset_0_-2px_0_#dc2626]'
                                : on
                                    ? ''
                                    : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:text-slate-900'
                        } focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/30 disabled:opacity-40 disabled:cursor-not-allowed ${keyClassName}`}
                    >
                        {it.icon && <span aria-hidden="true">{it.icon}</span>}
                        {it.label}
                    </button>
                );
            })}
        </div>
    );
}

// Convenience: turn the app's status arrays ({key,label,color,bg}) or
// option arrays ({value,label}) into ChoiceKeys items.
export const toKeyItems = (arr = []) => arr.map(o => ({
    value: o.value !== undefined ? o.value : o.key,
    label: o.label ?? String(o.value ?? o.key),
    color: o.color, bg: o.bg, icon: o.icon, disabled: o.disabled, title: o.title,
}));
